"use client";

import Image from "next/image";
import { Fragment, type ReactNode } from "react";
import { stegaClean } from "next-sanity";
import { cn } from "@/lib/utils";
import { resolveI18nText } from "@/sanity/globals/I18n";
import type { GROQImage, IAffiliateHeroItem } from "@/sanity/lib/definitions";

export function AffiliateHeroItemRow({ icon, text, className }: { icon?: GROQImage; text?: string; className?: string }) {
  return (
    <div className={cn("flex items-center gap-[1.5rem]", className)}>
      {icon && (
        <Image
          src={icon}
          alt=""
          className="!w-[2.375rem] shrink-0"
        />
      )}
      <span>{text}</span>
    </div>
  );
}

/**
 * The hero bullet list shared by every affiliate page, edited once in Sanity
 * under the global "Affiliate Hero Items". `children` is rendered at the end of
 * the same list, which is where the page's own recommendation row goes.
 */
export default function AffiliateHeroItems({
  items,
  language,
  className,
  children,
}: {
  items?: IAffiliateHeroItem[];
  language?: string;
  className?: string;
  children?: ReactNode;
}) {
  // stegaClean: in draft mode the CMS language carries invisible characters and
  // would miss the translation key (same guard as the Footer).
  const lang = stegaClean(language);

  // Resolve before filtering, so a row filled only in German still shows on a
  // German page.
  const rows = (items ?? [])
    .map((item) => ({ icon: item?.icon, text: resolveI18nText(item?.text, item?.textI18n, lang) }))
    .filter((row) => row.icon || stegaClean(row.text));

  return (
    <div className={cn("flex flex-col gap-[1.375rem] my-[5rem] w-[min(52.125rem,100%)]", className)}>
      {rows.map((row, index) => (
        <Fragment key={"affiliateHeroItem" + index}>
          <AffiliateHeroItemRow
            icon={row.icon}
            text={row.text}
          />
          {index !== rows.length - 1 && <hr className="w-full border-t border-black/10" />}
        </Fragment>
      ))}
      {children}
    </div>
  );
}
